import React, { useEffect, useRef, useState, useMemo } from "react";
import Chart from "chart.js/auto";
import "./AggregatedUserCharts.css";
import "chartjs-adapter-date-fns";

function AggregatedPersonalityCharts({ allUserData, startDate, endDate }) {
  const chartRef = useRef(null);
  const canvasRef = useRef(null);
  const scatterChartRef = useRef(null);
  const scatterCanvasRef = useRef(null);

  const [historyData, setHistoryData] = useState(null);

  const categories = useMemo(
    () => [
      "Extraversion",
      "Agreeableness",
      "Conscientiousness",
      "Emotional Stability",
      "Intellect/Imagination",
    ],
    []
  );

  const [startDateState, setStartDate] = useState("");
  const [endDateState, setEndDate] = useState("");

  const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === "string") return new Date(d);
    if (d && d.$date) return new Date(d.$date);
    try {
      return new Date(d);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    const url = `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/admin/users/personality-history`;
    fetch(url, { method: "GET", credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status}: ${text}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!mounted) return;
        setHistoryData(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error fetching personality history:", err);
        if (mounted) setHistoryData([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Radar chart —
  useEffect(() => {
    const source =
      historyData && historyData.length > 0 ? historyData : allUserData;

    if (!source || source.length === 0) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const totals = {};
    const counts = {};

    source.forEach((user) => {
      let latestObj = null;
      if (user.scores && user.scores.length > 0) {
        latestObj = user.scores[user.scores.length - 1];
      }
      categories.forEach((cat) => {
        const flattenedScore = user[cat];
        const flattenedMax = user[`${cat}_max`];

        let scoreVal = null;
        let maxVal = null;

        if (flattenedScore != null && flattenedMax != null) {
          scoreVal = Number(flattenedScore);
          maxVal = Number(flattenedMax);
        } else if (latestObj) {
          scoreVal = Number(latestObj.score?.[cat]);
          maxVal = Number(latestObj.maxScore?.[cat]);
        }

        if (!isNaN(scoreVal) && !isNaN(maxVal) && maxVal > 0) {
          totals[cat] = (totals[cat] || 0) + (scoreVal / maxVal) * 100;
          counts[cat] = (counts[cat] || 0) + 1;
        }
      });
    });

    const averages = categories.map((cat) =>
      counts[cat] > 0 ? totals[cat] / counts[cat] : 0
    );

    const data = {
      labels: categories,
      datasets: [
        {
          label: "Average Personality Scores (Normalised)",
          data: averages,
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderColor: "rgb(75, 192, 192)",
          pointBackgroundColor: "rgb(75, 192, 192)",
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    const config = {
      type: "radar",
      data,
      options: {
        responsive: true,
        plugins: {
          legend: { position: "top" },
        },
        scales: {
          r: {
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: { stepSize: 20 },
          },
        },
      },
    };

    if (chartRef.current) {
      chartRef.current.destroy();
    }
    if (canvasRef.current) {
      chartRef.current = new Chart(canvasRef.current, config);
    }
    // cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [historyData, allUserData, categories]);

  // Scatter chart - plot history data (all users, all entries, per category)
  useEffect(() => {
    const source = historyData && historyData.length > 0 ? historyData : [];

    if (!source || source.length === 0) {
      if (scatterChartRef.current) {
        scatterChartRef.current.destroy();
        scatterChartRef.current = null;
      }
      return;
    }

    const startDateObj = startDateState ? new Date(startDateState) : null;
    const endDateObj = endDateState
      ? new Date(new Date(endDateState).setHours(23, 59, 59, 999))
      : null;

    const perCategoryData = {};
    categories.forEach((cat) => {
      perCategoryData[cat] = [];
    });

    source.forEach((user) => {
      const scores = user.scores || [];
      scores.forEach((entry) => {
        const date = parseDate(entry.date);
        if (!date || isNaN(date)) return;

        // apply date filters
        if (startDateObj && date < startDateObj) return;
        if (endDateObj && date > endDateObj) return;

        categories.forEach((cat) => {
          const s = entry.score?.[cat];
          const m = entry.maxScore?.[cat];
          if (s != null && m != null && Number(m) > 0) {
            perCategoryData[cat].push({
              x: date,
              y: (Number(s) / Number(m)) * 100,
            });
          }
        });
      });
    });

    // datasets for each category with distinct colors
    const data = {
      datasets: categories.map((cat, i) => ({
        label: cat,
        data: perCategoryData[cat],
        backgroundColor: `hsl(${(i * 72) % 360}, 70%, 60%)`,
        borderColor: `hsl(${(i * 72) % 360}, 70%, 40%)`,
        showLine: false,
      })),
    };

    const scatterConfig = {
      type: "scatter",
      data,
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: "Personality Score History (Per Category %)",
          },
        },
        scales: {
          x: {
            type: "time",
            time: { unit: "day", displayFormats: { day: "dd/MM" } },
            title: { display: true, text: "Date" },
          },
          y: {
            title: { display: true, text: "Score (%)" },
            suggestedMin: 0,
            suggestedMax: 100,
          },
        },
      },
    };

    if (scatterChartRef.current) {
      scatterChartRef.current.destroy();
    }
    if (scatterCanvasRef.current) {
      scatterChartRef.current = new Chart(
        scatterCanvasRef.current,
        scatterConfig
      );
    }

    return () => {
      if (scatterChartRef.current) {
        scatterChartRef.current.destroy();
        scatterChartRef.current = null;
      }
    };
  }, [historyData, startDateState, endDateState, categories]);

  // Export functions
  const exportScatterToCSV = (chart, filename) => {
    if (!chart || !chart.data || !chart.data.datasets) return;

    const csvRows = [];
    const headers = ["Category", "Date", "Score (%)"];
    csvRows.push(headers.join(","));

    chart.data.datasets.forEach((dataset) => {
      dataset.data.forEach((point) => {
        const date =
          point.x instanceof Date
            ? point.x.toISOString()
            : new Date(point.x).toISOString();
        const row = [dataset.label, date, Number(point.y).toFixed(2)];
        csvRows.push(row.join(","));
      });
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportRadarToCSV = (chart, filename) => {
    if (!chart || !chart.data) return;
    const csvRows = [];
    const headers = ["Trait", ...chart.data.labels];
    csvRows.push(headers.join(","));

    chart.data.datasets.forEach((dataset) => {
      const row = [
        dataset.label,
        ...dataset.data.map((val) => Number(val).toFixed(2)),
      ];
      csvRows.push(row.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const noScores = !historyData || historyData.length === 0;
  return (
    <div
      className="user-charts"
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      {!noScores ? (
        <>
          {/* Radar */}
          <div>
            <h3>Averaged Recent Personality Scores For All Users</h3>
            <canvas ref={canvasRef}></canvas>
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => {
                  if (chartRef.current) {
                    const link = document.createElement("a");
                    link.href = chartRef.current.toBase64Image();
                    link.download = "averaged_personality_scores_chart.png";
                    link.click();
                  }
                }}
              >
                Download PNG
              </button>{" "}
              <button
                onClick={() => {
                  if (chartRef.current) {
                    exportRadarToCSV(
                      chartRef.current,
                      "personality_radar_chart.csv"
                    );
                  }
                }}
              >
                Export Radar Data to CSV
              </button>
            </div>
          </div>

          {/* Date pickers */}
          <div style={{ margin: "16px 0" }}>
            <label style={{ marginRight: "8px" }}>
              Start Date:
              <input
                type="date"
                value={startDateState}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setStartDate(newStart);

                  if (
                    endDateState &&
                    new Date(endDateState) < new Date(newStart)
                  ) {
                    setEndDate("");
                  }
                }}
                style={{ marginTop: "4px" }}
              />
            </label>
            <label>
              End Date:
              <input
                type="date"
                value={endDateState}
                min={startDateState || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ marginTop: "4px" }}
              />
            </label>
          </div>

          {startDate && endDate && new Date(endDate) < new Date(startDate) && (
            <div style={{ color: "red", marginBottom: "8px" }}>
              End date cannot be before start date.
            </div>
          )}

          {/* Scatter */}
          <div>
            <h3>Personality Score History</h3>
            <canvas ref={scatterCanvasRef} width={600} height={400}></canvas>
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => {
                  if (scatterChartRef.current) {
                    const link = document.createElement("a");
                    link.href = scatterChartRef.current.toBase64Image();
                    link.download = "personality_score_history_chart.png";
                    link.click();
                  }
                }}
              >
                Download PNG
              </button>{" "}
              <button
                onClick={() => {
                  if (scatterChartRef.current) {
                    exportScatterToCSV(
                      scatterChartRef.current,
                      "personality_scatter_chart.csv"
                    );
                  }
                }}
              >
                Export Scatter Data to CSV
              </button>
            </div>
          </div>
        </>
      ) : (
        <div>Loading charts...</div>
      )}
    </div>
  );
}

export default AggregatedPersonalityCharts;
